import { useEffect, useState } from "react";
import type { User } from "@revival/contracts";
import { ImageEditor } from "./Presentation";
import { useLeaveGuard } from "./Navigation";
import { api } from "./api";
import { VoiceTextField } from "./voice";

export default function ProfilePage({ user, contractor, onChanged, onDeleted, onBack }: {
  user: User;
  contractor?: { company: string; trade: string } | null;
  onChanged: (user: User) => void;
  onDeleted: () => void;
  onBack: () => void;
}) {
  const [name, setName] = useState(user.name);
  const [telephone, setTelephone] = useState(user.telephone);
  const [working, setWorking] = useState("");
  const [message, setMessage] = useState("");
  const [company, setCompany] = useState(contractor ?? null);

  useEffect(() => {
    if (user.role !== "contractor") return;
    let active = true;
    void api.ownContractorCompany().then((value) => { if (active && value) setCompany(value); }).catch(() => undefined);
    return () => { active = false; };
  }, [user.role]);

  async function save() {
    if (!name.trim()) return;
    setWorking("save"); setMessage("");
    try { const updated = await api.updateProfile(name.trim(), telephone.trim()); onChanged(updated); setMessage("Profile details saved. The administrator can see the amendment history."); }
    catch (error) { setMessage(error instanceof Error ? error.message : "Profile details could not be saved"); }
    finally { setWorking(""); }
  }

  async function remove() {
    if (!window.confirm("Archive your Site Snag account? You will immediately lose access, but an administrator can restore your profile and project history.")) return;
    setWorking("delete"); setMessage("");
    try { await api.deleteOwnProfile(); onDeleted(); }
    catch (error) { setMessage(error instanceof Error ? error.message : "Your account could not be deleted"); setWorking(""); }
  }

  useLeaveGuard(name !== user.name || telephone !== user.telephone, {
    title: 'Leave your profile changes?', blocked: Boolean(working), saveLabel: 'Save profile and leave',
    save: async () => { const updated = await api.updateProfile(name.trim(), telephone.trim()); onChanged(updated); },
    discard: async () => { setName(user.name); setTelephone(user.telephone); },
  });
  return <section className="profile-page">
    <button className="back-to-projects" onClick={onBack} type="button">← Back</button>
    <div className="page-title-row"><div><p className="eyebrow">MY ACCOUNT</p><h1>Profile</h1><p>Update the contact details used by your organisation.</p></div></div>
    {message && <div className="setup-message">{message}</div>}
    <article className="admin-card profile-card">
      <div className="profile-edit-layout"><div className="profile-fields"><VoiceTextField label="Name" value={name} onChange={setName} /><VoiceTextField inputMode="tel" label="Telephone" type="tel" value={telephone} onChange={setTelephone} /><label>Email address<input disabled value={user.email} /></label><label>Account type<input disabled value={user.role === "manager" ? "Site Manager" : user.role === "contractor" ? "Contractor" : "Administrator"} /></label>{user.role === "contractor" && <><label>Company<input disabled value={company?.company || "Company link unavailable"} /></label><label>Trade<input disabled value={company?.trade || "Not recorded"} /></label></>}</div><ImageEditor compact kind="user" id={user.id} label="Profile photograph" fallback={user.name.slice(0, 2).toUpperCase()} /></div>
      <button className="primary-button" disabled={working === "save" || !name.trim()} onClick={() => void save()} type="button">{working === "save" ? "Saving…" : "Save profile"}</button>
    </article>
    {user.role !== "administrator" && <article className="admin-card profile-delete-card"><h2>Archive my account</h2><p>This immediately disables your login while retaining a recoverable administrative backup and your project history.</p><button disabled={working === "delete"} onClick={() => void remove()} type="button">{working === "delete" ? "Archiving…" : "Archive and delete my access"}</button></article>}
  </section>;
}
